import { useState } from 'react'
import { useAuthState } from '@/hooks/useAuth'
import { collection, addDoc, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { AuthModal } from "@/components/auth/AuthModal"
import { CitySelector, type CityData } from '@/components/shared/CitySelector'
import { toast } from 'sonner'

interface ArtistWaitlistRequestProps {
  artistDisplayName: string
  onSuccess?: () => void
}

export function ArtistWaitlistRequest({ artistDisplayName, onSuccess }: ArtistWaitlistRequestProps) {
  const { user } = useAuthState()
  
  const [firstName, setFirstName] = useState<string>('')
  const [selectedCity, setSelectedCity] = useState<string>('')
  const [selectedCityData, setSelectedCityData] = useState<CityData | null>(null)
  const [isFlash, setIsFlash] = useState<boolean>(true)
  const [customIdea, setCustomIdea] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [confidenceLevel, setConfidenceLevel] = useState<number>(4)

  const handleCityChange = (cityName: string, cityData: CityData | null) => {
    setSelectedCity(cityName)
    setSelectedCityData(cityData)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!user) {
      setShowAuthModal(true)
      return
    }

    if (!firstName.trim()) {
      toast.error('Please enter your first name')
      return
    }

    if (!selectedCity || !selectedCityData) {
      toast.error('Please select a city')
      return
    }

    setIsSubmitting(true)

    try {
      const requestsRef = collection(db, 'guestSpotRequests')
      
      await addDoc(requestsRef, {
        userId: user.uid,
        firstName: firstName.trim(),
        artistInstagram: artistDisplayName,
        location: {
          city: selectedCityData.city,
          country: selectedCityData.country,
          lat: selectedCityData.latitude,
          lon: selectedCityData.longitude,
        },
        isFlash: isFlash,
        customIdea: !isFlash && customIdea.trim() ? customIdea.trim() : null,
        confidenceLevel: confidenceLevel,
        createdAt: Timestamp.now(),
      })

      toast.success('Request submitted!')
      
      // Reset form
      setFirstName('')
      setSelectedCity('')
      setSelectedCityData(null)
      setIsFlash(true)
      setCustomIdea('')
      setConfidenceLevel(4)
      
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Error submitting request:', error)
      toast.error('Failed to submit request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className="py-6 overflow-y-auto flex-1">
        <div className="phone-container-padded">
          <div className="mb-6">
            <h2 className="text-sm font-semibold mb-1">Global Waitlist</h2>
            <p className="text-sm text-muted-foreground">
              Let me know where to come next
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 w-full mx-auto">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>

            {/* City Selection */}
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <CitySelector
                value={selectedCity}
                onChange={handleCityChange}
                placeholder="Choose a city"
                className="w-full"
              />
            </div>

            {/* Flash or Custom Switch */}
            <div className="flex items-center justify-between">
              <Label htmlFor="booking-type">Booking Type</Label>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isFlash ? 'font-medium' : 'text-muted-foreground'}`}>
                  Flash
                </span>
                <Switch
                  id="booking-type"
                  checked={!isFlash}
                  onCheckedChange={(checked) => setIsFlash(!checked)}
                />
                <span className={`text-sm ${!isFlash ? 'font-medium' : 'text-muted-foreground'}`}>
                  Custom
                </span>
              </div>
            </div>

            {/* Custom Idea (optional, only shown if custom) */}
            {!isFlash && (
              <div className="space-y-2">
                <Label htmlFor="customIdea">Custom Idea (Optional)</Label>
                <Textarea
                  id="customIdea"
                  placeholder="Describe your tattoo idea..."
                  value={customIdea}
                  onChange={(e) => setCustomIdea(e.target.value)}
                  rows={4}
                />
              </div>
            )}

            {/* Confidence Level Slider */}
            <div className="space-y-3">
              <Label>
                How sure are you about wanting a tattoo from me?
              </Label>
              <div className="space-y-2 pt-2">
                <Slider
                  value={[confidenceLevel]}
                  onValueChange={(value) => setConfidenceLevel(value[0])}
                  min={1}
                  max={7}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </div>
      </div>
      
      <AuthModal 
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
      />
    </>
  )
}

